import React, { useEffect, useState, useRef } from 'react';
import Navbar from '../../component/navbar';
import Footer from '../../component/footer';
import { getAllProducts } from '../../redux/actions/storeActions';
import { getAllCategories } from '../../redux/actions/categoryActions';
import ProductCard from '../../component/productCard';
import { connect } from 'react-redux';
import {
	Box,
	SimpleGrid,
	Text,
	Button,
	Spinner,
	Center,
	Container,
	Flex,
	Image,
	IconButton,
	Badge,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import Alert from '../../component/alert';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';
const MotionBox = motion(Box);

const Store = ({ getAllProducts, products, categories, getAllCategories }: any) => {
	const [offset, setOffset] = useState(0);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [categoryID, setCategoryID] = useState('');
	const [subcategoryID, setSubcategoryID] = useState('');
	const [catloading, setCatLoading] = useState(true);
	const [selectedCategory, setSelectedCategory] = useState<any>(null);
	const [selectedSubcategory, setSelectedSubcategory] = useState<any>(null);

	const parentCategories = categories ? categories.filter((c: any) => !c.parent_id) : [];
	const subcategories = selectedCategory
		? categories.filter((c: any) => c.parent_id === selectedCategory.category_id)
		: [];

	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const load = async () => {
			setCatLoading(true);
			await getAllCategories();
			setCatLoading(false);
		};
		load();
	}, [getAllCategories]);

	useEffect(() => {
		const fetch = async () => {
			setLoading(true);
			await getAllProducts(offset, categoryID, subcategoryID || undefined);
			setLoading(false);
		};
		fetch();
	}, [getAllProducts, offset, categoryID, subcategoryID]);

	const handleSelectParent = (category: any) => {
		setOffset(0);
		setCategoryID(category.category_id);
		setSelectedCategory(category);
		setSubcategoryID('');
		setSelectedSubcategory(null);
	};

	const handleSelectSubcategory = (sub: any) => {
		setOffset(0);
		setSubcategoryID(sub.category_id);
		setSelectedSubcategory(sub);
	};

	const handleClearFilter = () => {
		setOffset(0);
		setCategoryID('');
		setSubcategoryID('');
		setSelectedCategory(null);
		setSelectedSubcategory(null);
	};

	const handleShowMore = async () => {
		setLoadingMore(true);
		await getAllProducts(offset + 10, categoryID, subcategoryID || undefined);
		setOffset((prev) => prev + 10);
		setLoadingMore(false);
	};

	const scroll = (direction: 'left' | 'right') => {
		if (containerRef.current) {
			containerRef.current.scrollBy({
				left: direction === 'left' ? -320 : 320,
				behavior: 'smooth',
			});
		}
	};

	const activeName = selectedSubcategory
		? selectedSubcategory.category_name
		: selectedCategory?.category_name;

	return (
		<>
			<Navbar />
			<Alert />

			{/* Category Header Bar */}
			<AnimatePresence>
				{selectedCategory && (
					<MotionBox
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: 'auto' }}
						exit={{ opacity: 0, height: 0 }}
						transition={{ duration: 0.25 }}
						overflow="hidden"
						bg="white"
						borderBottom="1px solid"
						borderColor="gray.200"
					>
						<Container maxW="container.xl">
							<Flex align="center" gap={4} py={3}>
								{(selectedSubcategory?.image || selectedCategory?.image) && (
									<Image
										src={`${BASE_URL}/api/image?filename=${selectedSubcategory?.image || selectedCategory?.image}`}
										alt={activeName}
										boxSize="48px"
										objectFit="contain"
										borderRadius="lg"
										flexShrink={0}
									/>
								)}
								<Box borderLeft="3px solid" borderColor="green.400" pl={3}>
									<Text fontWeight="bold" fontSize="md" color="gray.800" lineHeight="short">
										{activeName}
									</Text>
									{(selectedSubcategory?.description || selectedCategory?.description) && (
										<Text fontSize="xs" color="gray.500" noOfLines={1}>
											{selectedSubcategory?.description || selectedCategory?.description}
										</Text>
									)}
								</Box>
							</Flex>
						</Container>
					</MotionBox>
				)}
			</AnimatePresence>

			<Container maxW="container.xl" py={4}>

				{/* Category Carousel */}
				<Box position="relative" mb={2}>
					<IconButton
						icon={<ChevronLeftIcon fontSize="2xl" />}
						position="absolute"
						left={-4}
						top="50%"
						transform="translateY(-50%)"
						onClick={() => scroll('left')}
						bg="white"
						boxShadow="md"
						borderRadius="full"
						size="sm"
						zIndex={10}
						aria-label="Scroll left"
						_hover={{ bg: 'gray.100' }}
					/>

					<Flex
						overflowX="auto"
						px={6}
						py={3}
						gap={3}
						align="center"
						ref={containerRef}
						css={{
							'&::-webkit-scrollbar': { display: 'none' },
							'-ms-overflow-style': 'none',
							'scrollbar-width': 'none',
						}}
					>
						{/* "All" pill */}
						<Box
							flexShrink={0}
							px={4}
							py={2}
							borderRadius="full"
							border="2px solid"
							borderColor={!selectedCategory ? 'blue.500' : 'gray.200'}
							bg={!selectedCategory ? 'blue.500' : 'white'}
							color={!selectedCategory ? 'white' : 'gray.600'}
							fontWeight="semibold"
							fontSize="sm"
							cursor="pointer"
							transition="all 0.2s"
							_hover={{ borderColor: 'blue.400', bg: !selectedCategory ? 'blue.500' : 'gray.50' }}
							onClick={handleClearFilter}
							whiteSpace="nowrap"
						>
							All Products
						</Box>

						{!catloading && parentCategories.map((category: any) => {
							const isActive = selectedCategory?.category_id === category.category_id;
							return (
								<Box
									key={category.category_id}
									flexShrink={0}
									display="flex"
									flexDirection="column"
									alignItems="center"
									gap={1.5}
									px={3}
									py={2}
									borderRadius="xl"
									border="2px solid"
									borderColor={isActive ? 'blue.500' : 'gray.100'}
									bg={isActive ? 'blue.50' : 'white'}
									cursor="pointer"
									transition="all 0.2s"
									_hover={{ borderColor: 'blue.300', bg: 'blue.50' }}
									onClick={() => handleSelectParent(category)}
									minW="90px"
									textAlign="center"
								>
									<Box
										w="48px"
										h="48px"
										borderRadius="full"
										bg={isActive ? 'blue.100' : 'gray.50'}
										display="flex"
										alignItems="center"
										justifyContent="center"
										overflow="hidden"
									>
										{category.image ? (
											<Image
												src={`${BASE_URL}/api/image?filename=${category.image}`}
												alt={category.category_name}
												boxSize="36px"
												objectFit="contain"
											/>
										) : (
											<Box w="36px" h="36px" bg="gray.200" borderRadius="full" />
										)}
									</Box>
									<Text
										fontWeight={isActive ? 'bold' : 'medium'}
										fontSize="xs"
										color={isActive ? 'blue.700' : 'gray.700'}
										noOfLines={2}
										lineHeight="tight"
									>
										{category.category_name}
									</Text>
								</Box>
							);
						})}
					</Flex>

					<IconButton
						icon={<ChevronRightIcon fontSize="2xl" />}
						position="absolute"
						right={-4}
						top="50%"
						transform="translateY(-50%)"
						onClick={() => scroll('right')}
						bg="white"
						boxShadow="md"
						borderRadius="full"
						size="sm"
						zIndex={10}
						aria-label="Scroll right"
						_hover={{ bg: 'gray.100' }}
					/>
				</Box>

				{/* Subcategory pills */}
				<AnimatePresence>
					{subcategories.length > 0 && (
						<MotionBox
							initial={{ opacity: 0, height: 0 }}
							animate={{ opacity: 1, height: 'auto' }}
							exit={{ opacity: 0, height: 0 }}
							transition={{ duration: 0.2 }}
							overflow="hidden"
							mb={2}
						>
							<Flex gap={2} px={2} py={2} flexWrap="wrap">
								<Button
									size="xs"
									borderRadius="full"
									variant={!selectedSubcategory ? 'solid' : 'outline'}
									colorScheme="blue"
									onClick={() => { setSubcategoryID(''); setSelectedSubcategory(null); setOffset(0); }}
								>
									All
								</Button>
								{subcategories.map((sub: any) => (
									<Button
										key={sub.category_id}
										size="xs"
										borderRadius="full"
										variant={selectedSubcategory?.category_id === sub.category_id ? 'solid' : 'outline'}
										colorScheme="blue"
										onClick={() => handleSelectSubcategory(sub)}
									>
										{sub.category_name}
									</Button>
								))}
							</Flex>
						</MotionBox>
					)}
				</AnimatePresence>

				{/* Results header */}
				<Flex align="center" justify="space-between" mb={4} px={1}>
					<Flex align="center" gap={2}>
						<Text fontSize="sm" color="gray.500">
							{loading ? 'Loading…' : `${products?.length ?? 0} product${products?.length !== 1 ? 's' : ''}`}
						</Text>
						{selectedCategory && (
							<Badge colorScheme="blue" borderRadius="full" px={2} fontSize="xs">
								{activeName}
							</Badge>
						)}
					</Flex>
					{selectedCategory && (
						<Button size="xs" variant="ghost" colorScheme="gray" onClick={handleClearFilter}>
							Clear filter ×
						</Button>
					)}
				</Flex>

				{/* Product Grid */}
				{loading && products?.length === 0 ? (
					<Center h="50vh">
						<Spinner size="xl" color="blue.500" thickness="3px" />
					</Center>
				) : products && products.length > 0 ? (
					<>
						<MotionBox
							initial={{ opacity: 0, y: 8 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.35 }}
						>
							<SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={5}>
								{products.map((product: any) => (
									<ProductCard
										key={product.product_id}
										image={product.image}
										name={product.name}
										description={product.description}
										price={product.final_price}
										product_id={product.product_id}
										unit={product.unit}
										quantityPerUnit={product.quantity_per_unit}
										out_of_stock={product.out_of_stock}
										has_variants={product.has_variants}
									/>
								))}
							</SimpleGrid>
						</MotionBox>

						<Center mt={10} mb={6}>
							<Button
								size="md"
								colorScheme="blue"
								variant="outline"
								onClick={handleShowMore}
								isLoading={loadingMore}
								loadingText="Loading more…"
								borderRadius="full"
								px={8}
							>
								Load More Products
							</Button>
						</Center>
					</>
				) : (
					<Center h="40vh" flexDirection="column" gap={3}>
						<Text fontSize="xl" fontWeight="bold" color="gray.400">No products found</Text>
						<Text fontSize="sm" color="gray.400">Try a different category or clear your filter.</Text>
						<Button size="sm" colorScheme="blue" variant="outline" onClick={handleClearFilter}>
							Show all products
						</Button>
					</Center>
				)}
			</Container>

			<Footer />
		</>
	);
};

const mapStateToProps = (state: any) => ({
	products: state.products.products,
	categories: state.categories.categories,
});

const mapDispatchToProps = {
	getAllProducts,
	getAllCategories,
};

export default connect(mapStateToProps, mapDispatchToProps)(Store);
